import {
  DEFAULT_ASSISTANT_SETTINGS,
  type AssistantProvider,
  type AssistantSettings,
} from '@lixionary-jira/domain';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { AdminAssistant } from '../../api/assistant';
import { ApiError } from '../../api/client';
import { api, useAction } from '../../api/hooks';
import { useIsAdmin } from '../../api/session';
import { useCrumbs } from '../../layout/crumbs';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Alert, ErrorAlert, PageUnavailable, QueryView } from '../../ui/feedback';
import { Checkbox, Field, FormSection, Input, Select } from '../../ui/forms';
import { KeyValue, PageHeader, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';

const PROVIDERS: { value: AssistantProvider; label: string; baseUrl: string }[] = [
  { value: 'minimax', label: 'MiniMax', baseUrl: 'https://api.minimax.io/v1' },
  { value: 'openai_compatible', label: 'Other OpenAI-compatible API', baseUrl: '' },
];

/** Admin choice of the assistant's model and behavior. The API key is an operator secret, never shown here. */
export function AssistantSettingsPage() {
  usePageTitle('Ticket assistant');
  useCrumbs([{ label: 'Administration' }, { label: 'Ticket assistant' }]);
  const admin = useIsAdmin();
  const q = useQuery({
    queryKey: ['admin-assistant'],
    queryFn: () => api.get<AdminAssistant>('/admin/assistant'),
    enabled: admin,
  });
  if (!admin) return <PageUnavailable />;
  return (
    <div className="page narrow">
      <PageHeader
        title="Ticket assistant"
        subtitle="A read-only coding agent that explores each project's repository to verify requests, ask clarifying questions and draft implementation-ready tickets."
      />
      <QueryView query={q}>
        {(data) => <SettingsForm key={data.updatedAt ?? 'defaults'} data={data} />}
      </QueryView>
    </div>
  );
}

function SettingsForm({ data }: { data: AdminAssistant }) {
  const [s, setS] = useState<AssistantSettings>(data.settings);
  const set = <K extends keyof AssistantSettings>(k: K, v: AssistantSettings[K]) => setS({ ...s, [k]: v });
  const save = useAction(() => api.put<AdminAssistant>('/admin/assistant', s), {
    success: 'Assistant settings saved',
    invalidate: [['admin-assistant'], ['assistant-status']],
  });
  const test = useAction(() =>
    api.post<{ ok: boolean; latencyMs: number; reply?: string; error?: string; model?: string }>(
      '/admin/assistant/test',
    ),
  );
  const err = save.error instanceof ApiError ? save.error : null;
  const dirty = JSON.stringify(s) !== JSON.stringify(data.settings);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(undefined);
      }}
    >
      <FormSection title="Status">
        <div className="row">
          <Badge tone={data.available ? 'success' : 'warning'}>
            {data.available ? 'Available' : 'Unavailable'}
          </Badge>
          {data.reason && <span className="small muted">{data.reason}</span>}
        </div>
        <KeyValue
          items={[
            ['Model API key', data.apiKeyConfigured ? 'Configured (ASSISTANT_API_KEY)' : 'Not configured'],
            [
              'Repository token',
              data.gitTokenConfigured
                ? 'Configured (ASSISTANT_GIT_TOKEN)'
                : 'None (public repositories only)',
            ],
            ['Concurrent runs', String(data.concurrency)],
            ['Last changed', data.updatedAt ? <Timestamp value={data.updatedAt} /> : 'Using defaults'],
          ]}
        />
        {!data.apiKeyConfigured && (
          <Alert tone="info" title="An operator must provide the model API key">
            Set the <code>ASSISTANT_API_KEY</code> secret for the deployment (GitHub production secret). Keys
            are never stored in the database or shown in the browser.
          </Alert>
        )}
      </FormSection>
      <FormSection
        title="Model"
        description="Any provider with an OpenAI-compatible Chat Completions API and tool calling works."
      >
        <Checkbox
          label="Enable the ticket assistant"
          description="Offers chat drafting on the Create ticket page and reviews new tickets."
          checked={s.enabled}
          onChange={(v) => set('enabled', v)}
        />
        <Field label="Provider">
          {(p) => (
            <Select
              {...p}
              value={s.provider}
              onChange={(e) => {
                const next = PROVIDERS.find((x) => x.value === e.target.value)!;
                setS({ ...s, provider: next.value, baseUrl: next.baseUrl || s.baseUrl });
              }}
            >
              {PROVIDERS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label="Base URL"
          hint="The endpoint that serves /chat/completions."
          error={err?.fieldError('baseUrl')}
        >
          {(p) => (
            <Input {...p} value={s.baseUrl} onChange={(e) => set('baseUrl', e.target.value)} required />
          )}
        </Field>
        <Field
          label="Model"
          hint={`Default: ${DEFAULT_ASSISTANT_SETTINGS.model}. Use the provider's exact model identifier.`}
          error={err?.fieldError('model')}
        >
          {(p) => <Input {...p} value={s.model} onChange={(e) => set('model', e.target.value)} required />}
        </Field>
        <div className="grid-2">
          <Field label="Temperature" error={err?.fieldError('temperature')}>
            {(p) => (
              <Input
                {...p}
                type="number"
                min={0}
                max={2}
                step={0.1}
                value={s.temperature}
                onChange={(e) => set('temperature', Number(e.target.value))}
              />
            )}
          </Field>
          <Field
            label="Exploration steps"
            hint="Searches and file reads per answer."
            error={err?.fieldError('maxSteps')}
          >
            {(p) => (
              <Input
                {...p}
                type="number"
                min={2}
                max={60}
                value={s.maxSteps}
                onChange={(e) => set('maxSteps', Number(e.target.value))}
              />
            )}
          </Field>
        </div>
        <Field
          label="Context window (tokens)"
          hint={`The model's maximum context, e.g. ${DEFAULT_ASSISTANT_SETTINGS.contextWindow.toLocaleString('en-US')} for ${DEFAULT_ASSISTANT_SETTINGS.model}. Near 75% of it, older tool output is dropped and the exploration or conversation is summarized.`}
          error={err?.fieldError('contextWindow')}
        >
          {(p) => (
            <Input
              {...p}
              type="number"
              min={32000}
              step={1000}
              value={s.contextWindow}
              onChange={(e) => set('contextWindow', Number(e.target.value))}
            />
          )}
        </Field>
      </FormSection>
      <FormSection title="Behavior">
        <Checkbox
          label="Review tickets submitted through the form"
          description="Runs in the background after submission. Suggestions change nothing until the requester or an admin applies them."
          checked={s.reviewOnCreate}
          onChange={(v) => set('reviewOnCreate', v)}
        />
      </FormSection>
      <ErrorAlert error={save.error && !err?.details.some((d) => d.field) ? save.error : null} />
      {test.data &&
        (test.data.ok ? (
          <Alert tone="success" title={`Connected to ${test.data.model} in ${test.data.latencyMs} ms`}>
            Reply: {test.data.reply || '(empty)'}
          </Alert>
        ) : (
          <Alert tone="error" title="Connection test failed">
            {test.data.error}
          </Alert>
        ))}
      <ErrorAlert error={test.error} />
      <div className="form-actions">
        <Button
          variant="secondary"
          loading={test.isPending}
          disabled={dirty || !data.apiKeyConfigured}
          title={dirty ? 'Save first; the test uses the saved settings.' : undefined}
          onClick={() => test.mutate(undefined)}
        >
          Test connection
        </Button>
        <Button type="submit" loading={save.isPending} disabled={!dirty}>
          Save settings
        </Button>
      </div>
    </form>
  );
}
